import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CheckpointResponse } from "../../lib/schemas";
import type { ChatMessage } from "../hooks/useChat";
import { CheckpointCard } from "./CheckpointCard";
import { DishaContactBanner } from "./DishaContactBanner";
import { Message } from "./Message";
import { SuggestionChips } from "./SuggestionChips";
import { WarningMessage } from "./WarningMessage";

interface MessageListProps {
  messages: ChatMessage[];
  onSuggestionSelect: (suggestion: string) => void;
  isLoading: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  // Checkpoint props
  showCheckpoint: boolean;
  isCheckpointSubmitting: boolean;
  onCheckpointSubmit: (response: CheckpointResponse) => void;
  // Blocked state
  isBlocked: boolean;
}

export function MessageList({
  messages,
  onSuggestionSelect,
  isLoading,
  messagesEndRef,
  showCheckpoint,
  isCheckpointSubmitting,
  onCheckpointSubmit,
  isBlocked,
}: MessageListProps) {
  const [showChips, setShowChips] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const lastMessageIdRef = useRef<string | null>(null);
  const lastMessage = messages[messages.length - 1];
  const showSuggestions =
    (lastMessage?.role === "assistant" || lastMessage?.role === "warning") &&
    lastMessage?.suggestions &&
    lastMessage.suggestions.length > 0;

  // Check if Disha Contact was already shown in this session
  const hasShownContact = messages.some((m) => m.showDishaContact);

  // Reset chips visibility when new message arrives
  useEffect(() => {
    if (lastMessage && lastMessageIdRef.current !== lastMessage.id) {
      lastMessageIdRef.current = lastMessage.id;
      if (lastMessage.role === "assistant" || lastMessage.role === "warning") {
        // For warnings, show chips immediately (no typewriter effect)
        // For assistant messages, wait for typewriter to complete
        if (lastMessage.role === "warning") {
          setShowChips(true);
        } else {
          setShowChips(false);
        }
      }
    }
  }, [lastMessage]);

  // Track if user is at bottom using IntersectionObserver
  useEffect(() => {
    const target = messagesEndRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) {
          setIsAtBottom(entry.isIntersecting);
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [messagesEndRef]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messagesEndRef]);

  const handleTypingComplete = useCallback(() => {
    setShowChips(true);
    scrollToBottom();
  }, [scrollToBottom]);

  return (
    <div className="flex-1 overflow-y-auto px-4 py-6 scroll-smooth">
      {/* Messages with inline Disha contact banners */}
      {messages.map((message, index) => (
        <div key={message.id}>
          {/* Render warning messages differently */}
          {message.role === "warning" ? (
            <WarningMessage
              message={message.content}
              warningNumber={message.warningNumber ?? 1}
              isBlocked={message.isBlocked ?? false}
              timestamp={message.timestamp}
              showContact={!hasShownContact}
              hasShownContact={hasShownContact}
            />
          ) : (
            <Message
              message={message}
              isLatest={index === messages.length - 1}
              onType={
                index === messages.length - 1 ? scrollToBottom : undefined
              }
              onTypingComplete={
                index === messages.length - 1 ? handleTypingComplete : undefined
              }
            />
          )}
          {/* Show Disha contact banner after messages that need it */}
          {message.showDishaContact && <DishaContactBanner show={true} />}
        </div>
      ))}

      {/* Loading indicator */}
      {isLoading && (
        <div className="flex justify-start mb-4">
          <div className="backdrop-blur-sm bg-white/80 border border-stone-200/50 px-5 py-4 rounded-2xl rounded-bl-md shadow-md shadow-stone-900/5">
            <div className="flex gap-2 items-center mb-2">
              <div className="animate-pulse bg-linear-to-r from-emerald-400 h-2 rounded-full to-teal-500 w-2" />
              <span className="font-semibold text-emerald-700 text-xs tracking-wide uppercase">
                Disha
              </span>
            </div>
            <div className="flex gap-1.5 items-center">
              <div
                className="animate-bounce bg-stone-300 h-2 rounded-full w-2"
                style={{ animationDelay: "0ms" }}
              />
              <div
                className="animate-bounce bg-stone-300 h-2 rounded-full w-2"
                style={{ animationDelay: "150ms" }}
              />
              <div
                className="animate-bounce bg-stone-300 h-2 rounded-full w-2"
                style={{ animationDelay: "300ms" }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Suggestion chips - show after typing animation completes (unless checkpoint is showing) */}
      {showSuggestions &&
        !isLoading &&
        showChips &&
        !showCheckpoint &&
        !isBlocked && (
          <SuggestionChips
            suggestions={lastMessage.suggestions || []}
            onSelect={onSuggestionSelect}
            disabled={isLoading}
          />
        )}

      {/* Checkpoint card - show when triggered */}
      {showCheckpoint && !isLoading && showChips && (
        <CheckpointCard
          onSubmit={onCheckpointSubmit}
          isSubmitting={isCheckpointSubmitting}
        />
      )}

      {/* Scroll anchor */}
      <div ref={messagesEndRef} />

      {/* Scroll to bottom FAB - only show after typing is complete */}
      <AnimatePresence>
        {!isAtBottom && showChips && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            onClick={scrollToBottom}
            className="bg-emerald-600 bottom-32 fixed flex h-10 hover:bg-emerald-700 items-center justify-center right-4 rounded-full shadow-emerald-900/30 shadow-lg text-white transition-colors w-10 z-20"
            aria-label="Scroll to bottom"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-label="Scroll to bottom icon"
            >
              <title>Scroll to bottom</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
